import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import UpperCaseInput from '../components/UpperCaseInput';
import DniRutInput from '../components/DniRutInput';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Mail, UserCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false);
  const [newUserCredentials, setNewUserCredentials] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    dni: '',
    email: '',
    role: ''
  });
  const [dniValid, setDniValid] = useState(false);
  const [justification, setJustification] = useState('');

  // Obtener el rol efectivo (considerando simulación para DESARROLLO)
  const isDev = currentUser?.role === 'DESARROLLO';
  const isSimulating = isDev && currentUser?.simulated_role;
  const effectiveRole = isSimulating ? currentUser.simulated_role : currentUser?.role;

  useEffect(() => {
    fetchUsers();
  }, []);

  // Obtener roles disponibles según el rol efectivo del usuario
  // Jerarquía: DESARROLLO/MASTER > ADMIN > TRANSPORTISTA > CHOFER
  // DESARROLLO y MASTER pueden crear del mismo nivel y hacia abajo
  // El resto solo puede crear hacia abajo
  const getAvailableRoles = () => {
    if (effectiveRole === 'MASTER' || effectiveRole === 'DESARROLLO') {
      return ['DESARROLLO', 'MASTER', 'ADMIN', 'TRANSPORTISTA', 'CHOFER'];
    } else if (effectiveRole === 'ADMIN') {
      return ['TRANSPORTISTA', 'CHOFER'];
    } else if (effectiveRole === 'TRANSPORTISTA') {
      return ['CHOFER'];
    }
    return [];
  };

  // Obtener rol por defecto
  const getDefaultRole = () => {
    const roles = getAvailableRoles();
    if (effectiveRole === 'MASTER' || effectiveRole === 'DESARROLLO') return 'ADMIN';
    if (effectiveRole === 'ADMIN') return 'TRANSPORTISTA';
    return roles[0] || '';
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!dniValid) {
      toast.error('El DNI/RUT ingresado no es válido');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/users`, formData);
      const { user, temp_password } = response.data;
      
      setNewUserCredentials({
        id: user.id,
        email: user.email,
        full_name: user.full_name,
        role: user.role,
        temp_password: temp_password
      });
      
      setIsCreateModalOpen(false);
      setIsCredentialsModalOpen(true);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!justification.trim()) {
      toast.error('La justificación es obligatoria');
      return;
    }
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, {
        justification: justification.toUpperCase(),
        data: {
          full_name: formData.full_name?.toUpperCase() || null,
          dni: formData.dni?.toUpperCase() || null,
          email: formData.email || null
        }
      });
      toast.success('Usuario actualizado exitosamente');
      setIsEditModalOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      dni: '',
      email: '',
      role: getDefaultRole()
    });
    setDniValid(false);
    setJustification('');
    setSelectedUser(null);
  };

  const openCreateModal = () => {
    resetForm();
    setFormData(prev => ({ ...prev, role: getDefaultRole() }));
    setIsCreateModalOpen(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      full_name: user.full_name,
      dni: user.dni,
      email: user.email,
      role: user.role
    });
    setDniValid(true);
    setIsEditModalOpen(true);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'MASTER': return 'bg-purple-100 text-purple-800';
      case 'ADMIN': return 'bg-blue-100 text-blue-800';
      case 'TRANSPORTISTA': return 'bg-green-100 text-green-800';
      case 'CHOFER': return 'bg-orange-100 text-orange-800';
      case 'DESARROLLO': return 'bg-fuchsia-100 text-fuchsia-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = () => {
    if (effectiveRole === 'ADMIN') return 'Transportista';
    if (effectiveRole === 'TRANSPORTISTA') return 'Chofer';
    return 'Usuario';
  };

  // Verificar si el usuario actual puede editar/eliminar un usuario específico
  const canManageUser = (user) => {
    if (effectiveRole === 'MASTER' || effectiveRole === 'DESARROLLO') return true;
    if (user.created_by === currentUser.id) return true;
    if (user.id === currentUser.id) return true;
    return false;
  };

  const availableRoles = getAvailableRoles();

  return (
    <Layout>
      <div className="space-y-6" data-testid="users-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900 tracking-tight">Usuarios</h1>
            <p className="text-slate-600 mt-1">
              {(effectiveRole === 'MASTER' || effectiveRole === 'DESARROLLO')
                ? 'Gestión de todos los usuarios del sistema'
                : `Gestión de ${getRoleLabel().toLowerCase()}s`}
            </p>
          </div>
          {availableRoles.length > 0 && (
            <Button onClick={openCreateModal} data-testid="create-user-button" className="bg-primary hover:bg-primary-hover text-white rounded-sm">
              <Plus className="mr-2" size={18} />Crear {getRoleLabel()}
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
            <table className="w-full" data-testid="users-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">ID Usuario</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">DNI/RUT</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Rol</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50 transition-colors" data-testid={`user-row-${user.id}`}>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <UserCircle size={16} className="text-slate-400" />
                        <span className="text-sm font-mono font-medium text-slate-900">{user.id}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-sm text-slate-900">{user.full_name}</td>
                    <td className="px-6 py-3 text-sm font-mono text-slate-600">{user.dni}</td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <Mail size={14} className="text-slate-400" />
                        <span className="text-sm text-slate-600">{user.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-sm ${getRoleColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      {canManageUser(user) && (
                        <div className="flex items-center gap-2">
                          <Button onClick={() => openEditModal(user)} data-testid={`edit-user-${user.id}`} variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                            <Pencil size={16} />
                          </Button>
                          {user.id !== currentUser.id && (
                            <Button onClick={() => handleDelete(user.id)} data-testid={`delete-user-${user.id}`} variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal Crear Usuario */}
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogContent data-testid="create-user-modal" className="max-w-md">
            <DialogHeader><DialogTitle>Crear {getRoleLabel()}</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre Completo *</Label>
                <UpperCaseInput
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  data-testid="user-fullname-input"
                  placeholder="Nombre y apellido"
                />
              </div>
              <div className="space-y-2">
                <Label>DNI/RUT *</Label>
                <DniRutInput
                  value={formData.dni}
                  onChange={(e) => setFormData({ ...formData, dni: e.target.value })}
                  onValidChange={(valid) => setDniValid(valid)}
                  required
                  data-testid="user-dni-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="user-email-input"
                  placeholder="correo@ejemplo.com"
                />
              </div>
              {availableRoles.length > 1 && (
                <div className="space-y-2">
                  <Label>Rol *</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })} data-testid="user-role-select">
                    <SelectTrigger><SelectValue placeholder="Seleccionar rol" /></SelectTrigger>
                    <SelectContent>
                      {availableRoles.map(role => (
                        <SelectItem key={role} value={role}>{role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button type="submit" data-testid="submit-create-user" className="w-full bg-primary hover:bg-primary-hover text-white" disabled={!dniValid}>
                Crear {getRoleLabel()}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Editar Usuario */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent data-testid="edit-user-modal" className="max-w-md">
            <DialogHeader><DialogTitle>Editar Usuario</DialogTitle></DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-sm border border-slate-200">
                <p className="text-sm"><strong>ID:</strong> {selectedUser?.id}</p>
                <p className="text-xs text-slate-500"><strong>Rol:</strong> {selectedUser?.role}</p>
              </div>
              <div className="space-y-2">
                <Label>Nombre Completo</Label>
                <UpperCaseInput
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  data-testid="edit-user-fullname-input"
                />
              </div>
              <div className="space-y-2">
                <Label>DNI/RUT</Label>
                <DniRutInput
                  value={formData.dni}
                  onChange={(e) => setFormData({ ...formData, dni: e.target.value })}
                  onValidChange={(valid) => setDniValid(valid)}
                  data-testid="edit-user-dni-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="edit-user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-red-600">Justificación (obligatoria) *</Label>
                <UpperCaseInput
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  required
                  placeholder="Motivo de la edición"
                  data-testid="edit-user-justification-input"
                />
              </div>
              <Button type="submit" data-testid="submit-edit-user" className="w-full bg-primary hover:bg-primary-hover text-white">
                Actualizar Usuario
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Credenciales del Nuevo Usuario */}
        <Dialog open={isCredentialsModalOpen} onOpenChange={setIsCredentialsModalOpen}>
          <DialogContent data-testid="credentials-modal" className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-green-600">✓ {getRoleLabel()} Creado Exitosamente</DialogTitle>
            </DialogHeader>
            {newUserCredentials && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-sm p-4">
                  <p className="text-sm text-green-800 mb-3">Comparta estas credenciales con el usuario:</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Usuario:</span>
                      <span className="font-mono font-bold">{newUserCredentials.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Email:</span>
                      <span className="font-mono">{newUserCredentials.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Nombre:</span>
                      <span>{newUserCredentials.full_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Rol:</span>
                      <span className="font-medium">{newUserCredentials.role}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 border border-yellow-300 rounded-sm p-4">
                  <p className="text-sm font-semibold text-yellow-800 mb-2">Contraseña Temporal:</p>
                  <p className="font-mono text-2xl font-bold text-center text-yellow-900 bg-yellow-100 py-2 rounded-sm">
                    {newUserCredentials.temp_password}
                  </p>
                  <p className="text-xs text-yellow-700 mt-2 text-center">
                    El usuario deberá cambiar esta contraseña en su primer inicio de sesión
                  </p>
                </div>
                <Button 
                  onClick={() => {
                    setIsCredentialsModalOpen(false);
                    setNewUserCredentials(null);
                  }} 
                  className="w-full bg-primary hover:bg-primary-hover text-white"
                >
                  Entendido
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Users;
